import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Users,
  Mail,
  Phone,
  Building2,
  BadgeCheck,
  IdCard,
  RefreshCcw,
  Search,
  MoreVertical,
  Clock,
  CalendarClock,
  Timer,
} from "lucide-react";
import toast from "react-hot-toast";
import { getAuthJson, getAuthItem } from "../utils/authStorage";
import { getLocalDayKey } from "../lib/attendancePunchStorage";
import { usePermissions } from "../rbac/PermissionsContext";
import QuickRequestFormModal from "../components/Admin/attendance/components/QuickRequestFormModal";
import { employeeService } from "../services/api";
import {
  emptyAttendanceDeskDraft,
  submitChangeShiftRequest,
  submitRegulariseRequest,
  submitShortLeaveRequest,
} from "../lib/attendanceDeskRequests";
import { resolveEmployeeAttendanceRequestAccess } from "../lib/attendanceRequestAccess";

const STATUS_COLORS = {
  Active:
    "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",
  Confirmed:
    "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",
  Probation:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  "On Leave":
    "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800",
  Inactive:
    "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700",
  Resigned:
    "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",
  Relieved:
    "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",
  Terminated:
    "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",
};

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function isDirectPhotoUrl(value) {
  if (typeof value !== "string" || !value) return false;
  return (
    value.startsWith("data:") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  );
}

function MyTeam() {
  const { hasScreenAccess, isAdmin } = usePermissions();
  const [reportees, setReportees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [photoUrlMap, setPhotoUrlMap] = useState(() => new Map());
  const mountedRef = useRef(true);

  const [deskFormOpen, setDeskFormOpen] = useState(false);
  const [deskFormContext, setDeskFormContext] = useState(null);
  const [deskFormDraft, setDeskFormDraft] = useState(emptyAttendanceDeskDraft);
  const [deskFormSubmitting, setDeskFormSubmitting] = useState(false);

  const canChangeShiftRbac =
    isAdmin ||
    hasScreenAccess("my_team", "change_shift_for_reportee", "create") ||
    hasScreenAccess("my_team", "change_shift_for_reportee", "view") ||
    hasScreenAccess("ess_quick_panel", "change_shift", "view");

  const canRegulariseRbac =
    isAdmin ||
    hasScreenAccess("my_team", "regularise_for_reportee", "create") ||
    hasScreenAccess("my_team", "regularise_for_reportee", "view") ||
    hasScreenAccess("ess_quick_panel", "regularization_checks", "view");

  const canShortLeaveRbac =
    isAdmin ||
    hasScreenAccess("my_team", "short_leave_for_reportee", "create") ||
    hasScreenAccess("my_team", "short_leave_for_reportee", "view") ||
    hasScreenAccess("ess_quick_panel", "short_leave", "view");

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchReportees = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await employeeService.getMyReportees();
      if (!mountedRef.current) return;
      if (resp?.success) {
        setReportees(Array.isArray(resp.data) ? resp.data : []);
      } else {
        setReportees([]);
        setError(resp?.message || "Failed to load your team");
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setReportees([]);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load your team"
      );
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReportees();
  }, [fetchReportees]);

  useEffect(() => {
    if (!reportees.length) return;
    const toFetch = [];
    for (const emp of reportees) {
      if (!emp?.id) continue;
      const raw = emp.photo;
      if (!raw || typeof raw !== "string") continue;
      if (isDirectPhotoUrl(raw)) continue;
      if (photoUrlMap.get(emp.id)) continue;
      toFetch.push(emp.id);
    }
    if (!toFetch.length) return;

    let cancelled = false;
    Promise.all(
      toFetch.map((id) =>
        employeeService
          .getEmployeePhotoUrl(id)
          .then((resp) => ({
            id,
            url: resp?.url || resp?.data?.url || "",
          }))
          .catch(() => ({ id, url: "" }))
      )
    ).then((results) => {
      if (cancelled || !mountedRef.current) return;
      setPhotoUrlMap((prev) => {
        const next = new Map(prev);
        results.forEach((r) => {
          if (r?.id && r?.url) next.set(r.id, r.url);
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [reportees, photoUrlMap]);

  const filteredReportees = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return reportees;
    return reportees.filter((r) => {
      const haystack = [
        r.fullName,
        r.empCode,
        r.email,
        r.phone,
        r?.designation?.name,
        r?.department?.name,
        r?.branch?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [reportees, searchTerm]);

  const getPhotoSrc = (emp) => {
    const raw = emp?.photo;
    if (isDirectPhotoUrl(raw)) return raw;
    return photoUrlMap.get(emp?.id) || "";
  };

  const teamActionsFor = useCallback(
    (emp) => {
      const access = resolveEmployeeAttendanceRequestAccess(emp);
      return {
        changeShift: canChangeShiftRbac && !!access.changeShift,
        regularise: canRegulariseRbac && !!access.regularise,
        shortLeave: canShortLeaveRbac && !!access.shortLeave,
      };
    },
    [canChangeShiftRbac, canRegulariseRbac, canShortLeaveRbac]
  );

  const openDeskFormForReportee = useCallback((emp, actionLabel) => {
    if (!emp?.id) {
      toast.error("Employee record is incomplete.");
      return;
    }
    const manager = getAuthJson("currentUser") || {};
    setDeskFormContext({
      actionLabel,
      employeeId: String(emp.id),
      employeeCode:
        emp?.empCode || emp?.employeeCode || emp?.code || null,
      userId: emp?.user?.id || null,
      userName: emp.fullName || emp.empCode || "Team member",
      userEmail: emp.email || "",
      gender: emp.gender || "",
      requestedAt: Date.now(),
      raisedByManager: true,
      raisedByName: manager?.name || manager?.username || "Reporting Manager",
      raisedByEmployeeId:
        getAuthItem("employeeId") ||
        manager?.employeeId ||
        manager?.employee?.id ||
        null,
    });
    setDeskFormDraft(emptyAttendanceDeskDraft());
    setDeskFormOpen(true);
  }, []);

  const closeDeskForm = useCallback(() => {
    setDeskFormOpen(false);
    setDeskFormContext(null);
    setDeskFormSubmitting(false);
  }, []);

  const submitDeskFormForReportee = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (deskFormSubmitting || !deskFormContext?.employeeId) return;

      const actionLabel = deskFormContext.actionLabel;
      const formData = { ...deskFormDraft };

      if (actionLabel === "Change Shift") {
        if (!String(formData?.shift || "").trim()) {
          toast.error("Select a new shift.");
          return;
        }
        if (!String(formData?.date || "").trim()) {
          toast.error("Select an effective date.");
          return;
        }
      }
      if (actionLabel === "Short Leave") {
        if (!String(formData?.date || "").trim()) {
          toast.error("Select a date.");
          return;
        }
        if (!String(formData?.startTime || "").trim() || !String(formData?.endTime || "").trim()) {
          toast.error("Enter start and end time.");
          return;
        }
      }
      if (actionLabel === "Regularise checks") {
        if (!String(formData?.date || "").trim()) {
          toast.error("Select a date.");
          return;
        }
      }

      setDeskFormSubmitting(true);
      try {
        const common = {
          employeeId: deskFormContext.employeeId,
          employeeCode: deskFormContext.employeeCode || null,
          userName: deskFormContext.userName || "Team member",
          userId: deskFormContext.userId || null,
          userEmail: deskFormContext.userEmail || "",
          formData,
          mode: "rm_direct",
          raisedByName: deskFormContext.raisedByName || "Reporting Manager",
          raisedByEmployeeId: deskFormContext.raisedByEmployeeId || null,
        };

        if (actionLabel === "Change Shift") {
          await submitChangeShiftRequest(common);
          toast.success(
            `Change Shift applied for ${deskFormContext.userName} → ${String(formData.shift).trim()}`
          );
        } else if (actionLabel === "Short Leave") {
          await submitShortLeaveRequest(common);
          toast.success(`Short Leave applied for ${deskFormContext.userName}`);
        } else if (actionLabel === "Regularise checks") {
          await submitRegulariseRequest(common);
          toast.success(`Regularisation applied for ${deskFormContext.userName}`);
        } else {
          toast.error("Unsupported action.");
          return;
        }
        closeDeskForm();
      } catch (err) {
        toast.error(err?.message || "Failed to submit request");
      } finally {
        setDeskFormSubmitting(false);
      }
    },
    [deskFormSubmitting, deskFormContext, deskFormDraft, closeDeskForm]
  );

  const submitRegulariseFromModal = useCallback(
    (formData) => {
      setDeskFormDraft((prev) => ({ ...prev, ...formData }));
      // RegulariseChecksRequestModal calls onSubmit with formData directly
      (async () => {
        if (deskFormSubmitting || !deskFormContext?.employeeId) return;
        setDeskFormSubmitting(true);
        try {
          await submitRegulariseRequest({
            employeeId: deskFormContext.employeeId,
            employeeCode: deskFormContext.employeeCode || null,
            userName: deskFormContext.userName || "Team member",
            userId: deskFormContext.userId || null,
            userEmail: deskFormContext.userEmail || "",
            formData,
            mode: "rm_direct",
            raisedByName: deskFormContext.raisedByName || "Reporting Manager",
            raisedByEmployeeId: deskFormContext.raisedByEmployeeId || null,
          });
          toast.success(`Regularisation applied for ${deskFormContext.userName}`);
          closeDeskForm();
        } catch (err) {
          toast.error(err?.message || "Failed to submit regularisation");
        } finally {
          setDeskFormSubmitting(false);
        }
      })();
    },
    [deskFormSubmitting, deskFormContext, closeDeskForm]
  );

  return (
    <div className="p-6">
      <div className="mx-auto max-w-8xl h-full bg-white rounded-lg shadow-md overflow-hidden">
        <div className="w-full p-6">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-redhalo-red/10 text-redhalo-red dark:bg-redhalo-mint/15 dark:text-redhalo-mint">
              <Users size={20} />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                My Team
              </h1>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {loading
                  ? "Loading your reportees…"
                  : `${filteredReportees.length} of ${reportees.length} member${
                      reportees.length === 1 ? "" : "s"
                    } report to you`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search team…"
                className="h-9 w-52 rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-xs text-gray-800 outline-none focus:border-redhalo-red dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:text-gray-100"
              />
            </div>
            <button
              type="button"
              onClick={fetchReportees}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-[#3C3C4C] dark:text-gray-200 dark:hover:bg-[#2a2a2a]"
              title="Refresh"
            >
              <RefreshCcw size={14} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
            {error}
          </div>
        )}

        {loading ? (
          <SkeletonGrid />
        ) : filteredReportees.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 px-6 py-16 text-center dark:border-[#3C3C4C]">
            <Users className="mx-auto mb-3 text-gray-300 dark:text-gray-600" size={36} />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {searchTerm.trim() ? "No matching team members" : "No direct reportees yet"}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {searchTerm.trim()
                ? "Try a different search."
                : "Employees who list you as reporting manager will appear here."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredReportees.map((emp) => {
              const actions = teamActionsFor(emp);
              return (
                <ReporteeCard
                  key={emp.id}
                  employee={emp}
                  photoSrc={getPhotoSrc(emp)}
                  actions={actions}
                  onChangeShift={() => openDeskFormForReportee(emp, "Change Shift")}
                  onRegularise={() => openDeskFormForReportee(emp, "Regularise checks")}
                  onShortLeave={() => openDeskFormForReportee(emp, "Short Leave")}
                />
              );
            })}
          </div>
        )}
        </div>
      </div>

      <QuickRequestFormModal
        darkMode={false}
        isOpen={deskFormOpen}
        actionLabel={deskFormContext?.actionLabel || "Change Shift"}
        context={deskFormContext}
        quickActionDraft={deskFormDraft}
        setQuickActionDraft={setDeskFormDraft}
        maxDateToday={getLocalDayKey()}
        onClose={closeDeskForm}
        onSubmitQuickAction={submitDeskFormForReportee}
        onSubmitRegularise={submitRegulariseFromModal}
        isSubmitting={deskFormSubmitting}
      />
    </div>
  );
}

function ReporteeCard({
  employee,
  photoSrc,
  actions = {},
  onChangeShift,
  onRegularise,
  onShortLeave,
}) {
  const [imgError, setImgError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const statusKey = employee?.employmentStatus || "";
  const statusClass =
    STATUS_COLORS[statusKey] ||
    "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700";

  const showImage = Boolean(photoSrc) && !imgError;
  const hasAnyAction =
    !!actions.changeShift || !!actions.regularise || !!actions.shortLeave;

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-redhalo-red/40 hover:shadow-md dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:hover:border-redhalo-mint/40">
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-redhalo-red/15 to-redhalo-mint/20 text-sm font-semibold text-redhalo-red dark:from-redhalo-mint/20 dark:to-redhalo-red/10 dark:text-redhalo-mint">
          {showImage ? (
            <img
              src={photoSrc}
              alt=""
              className="h-full w-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            getInitials(employee?.fullName)
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3
                className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100"
                title={employee?.fullName || ""}
              >
                {employee?.fullName || "—"}
              </h3>
              <p
                className="mt-0.5 truncate text-xs font-medium text-gray-600 dark:text-gray-400"
                title={employee?.designation?.name || ""}
              >
                {employee?.designation?.name || "—"}
              </p>
            </div>
            {hasAnyAction && (
              <div className="relative flex-shrink-0" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  className="rounded-lg border border-gray-200 p-1.5 text-gray-600 transition hover:bg-gray-50 dark:border-[#3C3C4C] dark:text-gray-300 dark:hover:bg-[#2a2a2a]"
                  title="Team actions"
                  aria-label="Team actions"
                >
                  <MoreVertical size={16} />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 top-9 z-20 min-w-[12rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-[#3C3C4C] dark:bg-[#1D1D1D]">
                    {actions.changeShift && (
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          onChangeShift?.();
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs font-medium text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-[#2a2a2a]"
                      >
                        <Clock size={14} className="text-redhalo-red dark:text-redhalo-mint" />
                        Change Shift
                      </button>
                    )}
                    {actions.regularise && (
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          onRegularise?.();
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs font-medium text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-[#2a2a2a]"
                      >
                        <CalendarClock size={14} className="text-redhalo-red dark:text-redhalo-mint" />
                        Regularise checks
                      </button>
                    )}
                    {actions.shortLeave && (
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          onShortLeave?.();
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs font-medium text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-[#2a2a2a]"
                      >
                        <Timer size={14} className="text-redhalo-red dark:text-redhalo-mint" />
                        Short Leave
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          {statusKey && (
            <span
              className={`mt-1.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusClass}`}
            >
              <BadgeCheck size={10} />
              {statusKey}
            </span>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-1.5 border-t border-gray-100 pt-3 text-xs dark:border-[#3C3C4C]">
        <DetailRow
          icon={<IdCard size={12} />}
          label={employee?.empCode || "—"}
          title="Employee Code"
        />
        <DetailRow
          icon={<Building2 size={12} />}
          label={employee?.department?.name || "—"}
          title="Department"
        />
        <DetailRow
          icon={<Mail size={12} />}
          label={employee?.email || "—"}
          title={employee?.email || ""}
          truncate
        />
        <DetailRow
          icon={<Phone size={12} />}
          label={employee?.phone || "—"}
          title="Phone"
        />
      </div>
    </div>
  );
}

function DetailRow({ icon, label, title, truncate = false }) {
  return (
    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500 dark:bg-[#2a2a2a] dark:text-gray-400">
        {icon}
      </span>
      <span
        className={`${truncate ? "truncate" : ""} text-gray-700 dark:text-gray-300`}
        title={title}
      >
        {label}
      </span>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="h-44 animate-pulse rounded-2xl border border-gray-100 bg-gray-50 dark:border-[#3C3C4C] dark:bg-[#252525]"
        />
      ))}
    </div>
  );
}

export default MyTeam;
